/* Eleven Theory fit studio. No external runtime, no checkout or tracking changes.
 * Catalogue dimensions are supplied estimates. The calculation preserves the
 * original reference + allowance model; it does not predict thickness or taper.
 */
(function () {
  'use strict';
  if (window.ElevenTheoryFitStudio) {
    window.ElevenTheoryFitStudio.mount(document);
    return;
  }
  var catalogueRequests = new Map();
  var EPSILON = 1e-8;
  var defaultCompartments = [
    { name: 'Padded laptop sleeve', shortName: 'Padded sleeve', refH: 35.57, refW: 24.81 },
    { name: 'Laptop compartment', shortName: 'Rear compartment', refH: 38, refW: 26.5 },
    { name: 'Front admin sleeve', shortName: 'Admin sleeve', refH: 34.05, refW: 23.76 }
  ];
  var defaults = {
    reset: 'Reset', find_model: 'Find a model', use_measurements: 'Use measurements',
    search_label: 'Laptop brand or model', search_placeholder: 'Try MacBook, XPS, ThinkPad…',
    clear_search: 'Clear search', quick_label: 'A few familiar faces', selected_label: 'Now on the workbench',
    measure_heading: 'The outside edges. Lid closed.', fine_tune_heading: 'Fine-tune the footprint', long_edge: 'Longer edge', short_edge: 'Shorter edge',
    adjust_long: 'Adjust longer edge', adjust_short: 'Adjust shorter edge',
    your_device: 'Your device', estimated_limit: 'Estimated limit', reference_footprint: 'Reference footprint',
    show_allowance: 'Show allowance', hide_allowance: 'Hide allowance', diagram_caption: 'FOOTPRINT / TOP VIEW',
    empty_label: 'A good day starts with a good fit',
    fit_label: 'Room to carry', tight_label: 'A close fit', no_label: 'Beyond this area',
    fit_title: 'A little room.\nA better day.', tight_title: 'Almost there.\nLet’s double-check.', no_title: 'This space\nisn’t the one.',
    fit_description: 'Your device’s footprint is within this area’s estimated limit.',
    tight_description: 'Limited allowance. Check thickness and the opening with us.',
    try_area: 'Try the {area} instead.', no_placement: 'This footprint exceeds all three placement areas.',
    invalid: 'Enter positive measurements up to {maximum} per edge.',
    long_allowance: 'Longer edge allowance', short_allowance: 'Shorter edge allowance',
    long_exceeds: 'Longer edge exceeds by', short_exceeds: 'Shorter edge exceeds by',
    catalogue_loading: 'Loading the model catalogue. You can enter measurements below.',
    catalogue_ready: '{count} models. Catalogue dimensions are estimates; check your device revision.',
    catalogue_failed: 'Model search is unavailable. Use the quick picks or enter measurements below.',
    no_match: 'No match yet. Use measurements to check your device.',
    match_count: 'Showing {shown} of {count}. Add a model name to narrow the list.',
    compare_result: '{device} / {height} × {width} {unit}. Explore each area above; compare the details here.',
    explore_area: 'Explore {area}',
    diagram_empty: 'Select a laptop to compare its footprint with the compartment outline.',
    diagram_description: 'Your laptop is {height} by {width}. {area} estimated limit is {maxHeight} by {maxWidth}. Layers are shown at the same scale; thickness and opening taper are not modelled.',
    method_policy: 'We compare a closed device’s two outside edges with each area separately. The estimated limit adds {clearance} cm on each side of the reference footprint. A result is close when it extends beyond the reference and comes within {snug} cm of a limit.'
  };

  function finiteInRange(value, min, max, fallback) {
    var number = Number(value);
    return Number.isFinite(number) && number >= min && number <= max ? number : fallback;
  }
  function rounded(number, places) { return String(Number(number.toFixed(places === undefined ? 2 : places))); }
  function evaluate(h, w, compartments, snug) {
    if (!Number.isFinite(h) || !Number.isFinite(w) || h <= 0 || w <= 0) return [];
    var height = Math.max(h, w), width = Math.min(h, w);
    return compartments.map(function (compartment) {
      var fitsH = height <= compartment.maxH + EPSILON;
      var fitsW = width <= compartment.maxW + EPSILON;
      var overRef = height > compartment.refH + EPSILON || width > compartment.refW + EPSILON;
      var nearMax = compartment.maxH - height <= snug + EPSILON || compartment.maxW - width <= snug + EPSILON;
      return { compartment: compartment, fitsH: fitsH, fitsW: fitsW, state: !fitsH || !fitsW ? 'no' : overRef && nearMax ? 'tight' : 'fit' };
    });
  }
  function bestPlacement(results) {
    return results.find(function (result) { return result.state === 'fit'; }) || results.find(function (result) { return result.state === 'tight'; });
  }
  function catalogue(url) {
    if (!url || !window.fetch) return Promise.reject(new Error('Catalogue unavailable'));
    if (!catalogueRequests.has(url)) {
      catalogueRequests.set(url, fetch(url, { credentials: 'same-origin' }).then(function (response) {
        if (!response.ok) throw new Error('Catalogue unavailable');
        return response.json();
      }).then(function (data) {
        if (!Array.isArray(data)) throw new Error('Invalid catalogue');
        var valid = data.filter(function (item) {
          return item && typeof item.name === 'string' && typeof item.brand === 'string' && Number.isFinite(item.h) && Number.isFinite(item.w) && item.h > 0 && item.w > 0 && item.h <= 60 && item.w <= 60;
        });
        if (!valid.length) throw new Error('Empty catalogue');
        return valid;
      }));
    }
    return catalogueRequests.get(url);
  }

  function init(root) {
    if (!root || root.dataset.fitReady === 'true') return;
    var one = function (selector) { return root.querySelector(selector); };
    var all = function (selector) { return Array.prototype.slice.call(root.querySelectorAll(selector)); };
    var fields = { h: one('[data-edge="h"]'), w: one('[data-edge="w"]') };
    var search = one('[data-fit-search]');
    var list = one('[data-fit-list]');
    var listWrap = one('[data-fit-results]');
    var cards = all('[data-fit-compartment]');
    var areaTabs = one('[data-fit-area-tabs]');
    var diagram = one('[data-fit-diagram]');
    if (!fields.h || !fields.w || !search || !list || !listWrap || !cards.length || !areaTabs || !diagram) return;
    var clearance = finiteInRange(root.dataset.clearance, 0, 2, 0.5);
    var snug = finiteInRange(root.dataset.snug, 0, 1, 0.3);
    var config = {};
    try { config = JSON.parse((one('[data-fit-copy]') || {}).textContent || '{}'); } catch (_) { /* Visible Liquid fallback stays intact. */ }
    var copy = Object.assign({}, defaults);
    try {
      var overrides = JSON.parse(config.overrides || '{}');
      if (overrides && typeof overrides === 'object' && !Array.isArray(overrides)) {
        Object.keys(defaults).forEach(function (key) { if (typeof overrides[key] === 'string' && overrides[key].trim()) copy[key] = overrides[key]; });
      }
    } catch (_) { /* Bad editor text must never stop the calculator. */ }
    function message(key, params) {
      var text = copy[key] || key;
      if (params) Object.keys(params).forEach(function (name) { text = text.split('{' + name + '}').join(String(params[name])); });
      return text;
    }
    function setText(selector, text) { var node = one(selector); if (node) node.textContent = text; }
    function setHidden(selector, hidden) { var node = one(selector); if (node) node.hidden = hidden; }
    var emptyTitle = config.empty_title || (one('[data-fit-result-title]') || {}).textContent || 'What’s coming with you?';
    var emptyDescription = config.empty_description || (one('[data-fit-result-description]') || {}).textContent || '';
    var measureNote = config.measure_note || (one('[data-fit-note]') || {}).textContent || '';
    var compareDefault = (one('[data-fit-comparison-copy]') || {}).textContent || '';
    var compartments = cards.slice(0, 3).map(function (card, index) {
      var fallback = defaultCompartments[index];
      var refH = finiteInRange(card.dataset.refH, 0.01, 60, fallback.refH);
      var refW = finiteInRange(card.dataset.refW, 0.01, 60, fallback.refW);
      var long = Math.max(refH, refW), short = Math.min(refH, refW);
      return { name: card.dataset.name || fallback.name, shortName: card.dataset.shortName || fallback.shortName, refH: long, refW: short, maxH: long + clearance * 2, maxW: short + clearance * 2 };
    });
    var state = { cm: { h: null, w: null }, unit: 'cm', selected: null, area: 0, reference: false, mode: 'search', query: '', active: -1, open: false };
    var models = [], shown = [], totalMatches = 0, catalogueState = 'loading';
    var units = function () { return state.unit === 'cm' ? 1 : 2.54; };
    var measurement = function (number) { return rounded(number / units()) + ' ' + state.unit; };
    function isInvalid() { return ['h', 'w'].some(function (key) { var value = state.cm[key]; return value !== null && (!Number.isFinite(value) || value <= 0 || value > 60); }); }
    function isReady() { return !isInvalid() && state.cm.h !== null && state.cm.w !== null; }
    function writeInputs() {
      ['h', 'w'].forEach(function (edge) { fields[edge].value = state.cm[edge] === null ? '' : rounded(state.cm[edge] / units(), 3); });
    }
    function makeSvg(paths, viewBox) {
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', viewBox || '0 0 24 24'); svg.setAttribute('fill', 'none'); svg.setAttribute('aria-hidden', 'true');
      paths.forEach(function (definition) {
        var node = document.createElementNS('http://www.w3.org/2000/svg', definition.tag || 'path');
        Object.keys(definition).forEach(function (key) { if (key !== 'tag') node.setAttribute(key, definition[key]); });
        svg.appendChild(node);
      });
      return svg;
    }
    function compartmentIcon(index) {
      return makeSvg([{ d: 'M8 9V7a8 8 0 0 1 16 0v2M6 10h20l2 19H4Z', opacity: '.45' }, { d: index === 0 ? 'M10 11h12v14H10Z' : index === 1 ? 'M7 10h18l1 17H6Z' : 'M9 18h14v9H9Z' }], '0 0 32 32');
    }
    function statusIcon(status) {
      return makeSvg(status === 'no' ? [{ d: 'm7 7 10 10M17 7 7 17' }] : status === 'tight' ? [{ d: 'M12 5v8' }, { tag: 'circle', cx: '12', cy: '18', r: '.8', fill: 'currentColor', stroke: 'none' }] : [{ d: 'm5 12 4 4L19 6' }]);
    }
    function closeSearch() {
      state.open = false; state.active = -1; listWrap.hidden = true;
      search.setAttribute('aria-expanded', 'false'); search.removeAttribute('aria-activedescendant');
    }
    function clearMatches() {
      shown = []; totalMatches = 0;
      while (list.firstChild) list.removeChild(list.firstChild);
    }
    function choose(model) {
      state.selected = model; state.cm = { h: model.h, w: model.w }; state.query = ''; search.value = ''; closeSearch(); clearMatches();
      var best = bestPlacement(evaluate(model.h, model.w, compartments, snug));
      if (best) state.area = compartments.indexOf(best.compartment);
      writeInputs(); render();
    }
    function updateActive() {
      all('[data-model-index]').forEach(function (option, index) {
        var active = index === state.active;
        option.classList.toggle('is-active', active); option.setAttribute('aria-selected', String(active));
      });
      var option = state.active >= 0 ? list.children[state.active] : null;
      if (option) {
        search.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else search.removeAttribute('aria-activedescendant');
    }
    function renderMatches() {
      var tokens = state.query.toLowerCase().trim().split(/\s+/).filter(Boolean);
      var matches = tokens.length ? models.filter(function (model) {
        var name = (model.brand + ' ' + model.name).toLowerCase();
        return tokens.every(function (token) { return name.includes(token); });
      }) : [];
      totalMatches = matches.length; shown = matches.slice(0, 14);
      while (list.firstChild) list.removeChild(list.firstChild);
      shown.forEach(function (model, index) {
        var option = document.createElement('div'); option.id = root.id + '-option-' + index; option.dataset.modelIndex = String(index);
        option.setAttribute('role', 'option'); option.setAttribute('aria-selected', 'false');
        var name = document.createElement('span'); name.textContent = model.name;
        var brand = document.createElement('small'); brand.textContent = model.brand;
        option.appendChild(name); option.appendChild(brand); list.appendChild(option);
      });
      if (!shown.length) {
        var note = document.createElement('p'); note.textContent = message(catalogueState === 'loading' ? 'catalogue_loading' : catalogueState === 'failed' ? 'catalogue_failed' : 'no_match'); list.appendChild(note);
      }
      var visible = state.open && tokens.length > 0;
      listWrap.hidden = !visible; search.setAttribute('aria-expanded', String(visible));
      setText('[data-fit-result-count]', message('match_count', { shown: shown.length, count: totalMatches }));
      setHidden('[data-fit-result-count]', totalMatches <= 14);
      setHidden('[data-fit-clear-search]', !state.query);
      updateActive();
    }
    function draw(ready, current) {
      var compartment = compartments[state.area];
      var tall = ready ? Math.max(state.cm.h, state.cm.w) : 33;
      var wide = ready ? Math.min(state.cm.h, state.cm.w) : 23;
      var scale = Math.min(290 / Math.max(39, tall, compartment.maxH), 320 / Math.max(28, wide, compartment.maxW));
      var height = tall * scale, width = wide * scale;
      function rect(selector, h, w) {
        var node = one(selector); if (!node) return;
        node.setAttribute('x', String(256 - w * scale / 2)); node.setAttribute('y', String(204 - h * scale / 2));
        node.setAttribute('width', String(w * scale)); node.setAttribute('height', String(h * scale));
      }
      rect('[data-fit-limit]', compartment.maxH, compartment.maxW);
      rect('[data-fit-reference]', compartment.refH, compartment.refW);
      rect('[data-fit-device]', tall, wide);
      var limit = one('[data-fit-limit]'), ref = one('[data-fit-reference]');
      var hatch = diagram.querySelector('pattern[id^="FitHatch-"]');
      if (limit) limit.setAttribute('fill', state.reference && hatch ? 'url(#' + hatch.id + ')' : '#f2f0e905');
      if (ref) ref.classList.toggle('is-shown', state.reference);
      var deviceBase = one('[data-fit-device-base]');
      if (deviceBase) deviceBase.setAttribute('d', 'M' + (256 - width / 2 + 8) + ' ' + (204 + height / 2 - 7) + 'H' + (256 + width / 2 - 8));
      var longLine = one('[data-fit-line-long]'), shortLine = one('[data-fit-line-short]');
      if (longLine) longLine.setAttribute('d', 'M58 ' + (204 - height / 2) + 'V' + (204 + height / 2) + 'm-4 0h8M54 ' + (204 - height / 2) + 'h8');
      if (shortLine) shortLine.setAttribute('d', 'M' + (256 - width / 2) + ' 373H' + (256 + width / 2) + 'm0-4v8M' + (256 - width / 2) + ' 369v8');
      setText('[data-fit-short-text]', ready ? measurement(wide) : message('short_edge').toUpperCase());
      setText('[data-fit-long-text]', ready ? measurement(tall) : message('long_edge').toUpperCase());
      diagram.classList.toggle('is-empty', !ready); diagram.classList.toggle('is-no', Boolean(current && current.state === 'no'));
      diagram.setAttribute('aria-label', ready ? message('diagram_description', { height: measurement(tall), width: measurement(wide), area: compartment.name, maxHeight: measurement(compartment.maxH), maxWidth: measurement(compartment.maxW) }) : message('diagram_empty'));
    }
    function render() {
      var invalid = isInvalid(), ready = isReady();
      var results = ready ? evaluate(state.cm.h, state.cm.w, compartments, snug) : [];
      var best = bestPlacement(results), current = results[state.area], comp = compartments[state.area];
      var long = ready ? Math.max(state.cm.h, state.cm.w) : null, short = ready ? Math.min(state.cm.h, state.cm.w) : null;
      all('[data-mode]').forEach(function (button) { button.setAttribute('aria-pressed', String(button.dataset.mode === state.mode)); });
      setHidden('[data-fit-model-entry]', state.mode !== 'search');
      var measurementSection = one('[data-fit-measurement]'); if (measurementSection) measurementSection.classList.toggle('is-primary', state.mode === 'measure');
      setHidden('[data-fit-selected]', !state.selected); setText('[data-fit-selected-name]', state.selected ? state.selected.name : '');
      all('[data-unit]').forEach(function (button) { button.setAttribute('aria-pressed', String(button.dataset.unit === state.unit)); });
      all('[data-fit-unit]').forEach(function (node) { node.textContent = state.unit; });
      all('[data-quick-name]').forEach(function (button) { button.setAttribute('aria-pressed', String(Boolean(state.selected && button.dataset.quickName === state.selected.name))); });
      all('[data-area]').forEach(function (button) {
        var index = Number(button.dataset.area); button.setAttribute('aria-pressed', String(index === state.area));
        var oldStatus = button.querySelector('[data-fit-area-status]'); if (oldStatus) oldStatus.remove();
        if (results[index]) { var icon = statusIcon(results[index].state); icon.setAttribute('data-fit-area-status', ''); button.appendChild(icon); }
      });
      setText('[data-fit-measure-title]', message(state.mode === 'measure' ? 'measure_heading' : 'fine_tune_heading')); 
      ['h', 'w'].forEach(function (edge) {
        var value = state.cm[edge], bad = value !== null && (!Number.isFinite(value) || value <= 0 || value > 60);
        fields[edge].max = String(60 / units()); fields[edge].setAttribute('aria-invalid', String(bad));
        fields[edge].placeholder = rounded((edge === 'h' ? 33 : 23) / units());
        var slider = one('[data-slide="' + edge + '"]');
        if (slider) { var displayValue = value && Number.isFinite(value) ? value : edge === 'h' ? 33 : 23; slider.value = String(Math.max(1, Math.min(60, displayValue))); slider.setAttribute('aria-valuetext', measurement(displayValue)); }
      });
      setText('[data-fit-note]', invalid ? message('invalid', { maximum: measurement(60) }) : measureNote);
      var note = one('[data-fit-note]'); if (note) note.classList.toggle('is-error', invalid);
      setText('[data-fit-result-label]', current ? message(current.state + '_label') : message('empty_label'));
      setText('[data-fit-result-title]', current ? message(current.state + '_title') : emptyTitle);
      setText('[data-fit-result-description]', current ? current.state === 'fit' ? message('fit_description') : current.state === 'tight' ? message('tight_description') : best ? message('try_area', { area: best.compartment.name.toLowerCase() }) : message('no_placement') : emptyDescription);
      ['h', 'w'].forEach(function (edge) {
        var length = edge === 'h' ? long : short, limit = edge === 'h' ? comp.maxH : comp.maxW;
        var exceeds = length !== null && length > limit + EPSILON;
        setText('[data-fit-margin-label="' + edge + '"]', message((edge === 'h' ? 'long' : 'short') + (exceeds ? '_exceeds' : '_allowance')));
        setText('[data-fit-margin="' + edge + '"]', length === null ? '—' : measurement(Math.abs(limit - length)));
        var value = one('[data-fit-margin="' + edge + '"]'); if (value) value.classList.toggle('is-over', exceeds);
      });
      var allowance = one('[data-fit-allowance]');
      if (allowance) {
        allowance.setAttribute('aria-pressed', String(state.reference)); allowance.textContent = message(state.reference ? 'hide_allowance' : 'show_allowance') + ' ';
        var sign = document.createElement('span'); sign.setAttribute('aria-hidden', 'true'); sign.textContent = state.reference ? '−' : '+'; allowance.appendChild(sign);
      }
      cards.forEach(function (card, index) {
        var compartment = compartments[index]; if (!compartment) return;
        card.classList.toggle('is-selected', state.area === index);
        var reference = card.querySelector('[data-fit-reference-text]'); if (reference) reference.textContent = rounded(compartment.refH / units()) + ' × ' + rounded(compartment.refW / units()) + ' ' + state.unit;
        var explore = card.querySelector('[data-fit-explore]'); if (explore) { explore.setAttribute('aria-pressed', String(state.area === index)); explore.setAttribute('aria-label', message('explore_area', { area: compartment.name })); }
        var status = card.querySelector('[data-fit-card-status]');
        if (status) { status.hidden = !results[index]; status.textContent = results[index] ? message(results[index].state + '_label') : ''; status.className = 'etf-card-status' + (results[index] ? ' is-' + results[index].state : ''); if (results[index]) status.insertBefore(statusIcon(results[index].state), status.firstChild); }
      });
      setText('[data-fit-comparison-copy]', ready ? message('compare_result', { device: state.selected ? state.selected.name : message('your_device'), height: rounded(long / units()), width: rounded(short / units()), unit: state.unit }) : compareDefault);
      setHidden('[data-fit-clear-search]', !state.query);
      draw(ready, current);
    }

    compartments.forEach(function (compartment, index) {
      var button = document.createElement('button'); button.type = 'button'; button.dataset.area = String(index); button.setAttribute('aria-pressed', String(index === 0));
      var text = document.createElement('span'), number = document.createElement('small'), name = document.createElement('span');
      number.textContent = '0' + (index + 1); name.textContent = compartment.shortName;
      text.appendChild(number); text.appendChild(name); button.appendChild(compartmentIcon(index)); button.appendChild(text); areaTabs.appendChild(button);
    });
    all('[data-copy]').forEach(function (node) { var key = node.dataset.copy; if (Object.prototype.hasOwnProperty.call(copy, key)) node.textContent = copy[key]; });
    search.placeholder = message('search_placeholder');
    var clearSearch = one('[data-fit-clear-search]'); if (clearSearch) clearSearch.setAttribute('aria-label', message('clear_search'));
    all('[data-slide]').forEach(function (slider) { slider.setAttribute('aria-label', message(slider.dataset.slide === 'h' ? 'adjust_long' : 'adjust_short')); });
    setText('[data-fit-method-policy]', message('method_policy', { clearance: rounded(clearance), snug: rounded(snug) }));
    root.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var button = event.target.closest('button');
      if (!button || !root.contains(button)) {
        var option = event.target.closest('[data-model-index]');
        if (option && root.contains(option) && shown[Number(option.dataset.modelIndex)]) choose(shown[Number(option.dataset.modelIndex)]);
        return;
      }
      if (button.hasAttribute('data-fit-reset')) {
        state.cm = { h: null, w: null }; state.unit = 'cm'; state.selected = null; state.area = 0; state.reference = false; state.mode = 'search'; state.query = ''; search.value = ''; closeSearch(); clearMatches(); writeInputs(); render(); search.focus(); return;
      }
      if (button.dataset.mode) { state.mode = button.dataset.mode; closeSearch(); render(); return; }
      if (button.dataset.unit) { state.unit = button.dataset.unit === 'in' ? 'in' : 'cm'; writeInputs(); render(); return; }
      if (button.hasAttribute('data-quick-name')) {
        var model = models.find(function (item) { return item.name === button.dataset.quickName; }) || { name: button.dataset.quickName, h: Number(button.dataset.h), w: Number(button.dataset.w) };
        if (Number.isFinite(model.h) && Number.isFinite(model.w)) choose(model); return;
      }
      if (button.hasAttribute('data-fit-clear-search')) { state.query = ''; search.value = ''; closeSearch(); clearMatches(); render(); search.focus(); return; }
      if (button.hasAttribute('data-fit-allowance')) { state.reference = !state.reference; render(); return; }
      if (button.hasAttribute('data-area') || button.hasAttribute('data-fit-explore')) {
        var next = Number(button.hasAttribute('data-area') ? button.dataset.area : button.dataset.fitExplore);
        if (compartments[next]) state.area = next;
        render();
        if (button.hasAttribute('data-fit-explore')) {
          var workbench = one('.etf-workspace');
          if (workbench) workbench.scrollIntoView({ behavior: window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        }
      }
    });
    root.addEventListener('input', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (target === search) { state.query = search.value; state.open = true; state.active = -1; renderMatches(); return; }
      var edge = target.dataset.edge;
      if (edge === 'h' || edge === 'w') { state.selected = null; state.cm[edge] = target.value === '' ? null : Number(target.value) * units(); render(); return; }
      edge = target.dataset.slide;
      if (edge === 'h' || edge === 'w') {
        state.selected = null; if (state.cm.h === null) state.cm.h = 33; if (state.cm.w === null) state.cm.w = 23;
        state.cm[edge] = Number(target.value); writeInputs(); render();
      }
    });
    search.addEventListener('focus', function () { if (state.query.trim()) { state.open = true; renderMatches(); } });
    search.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { closeSearch(); return; }
      if (event.key === 'ArrowDown' && shown.length) { event.preventDefault(); state.open = true; state.active = Math.min(state.active + 1, shown.length - 1); listWrap.hidden = false; search.setAttribute('aria-expanded', 'true'); updateActive(); }
      if (event.key === 'ArrowUp' && shown.length) { event.preventDefault(); state.open = true; state.active = Math.max(state.active - 1, 0); listWrap.hidden = false; search.setAttribute('aria-expanded', 'true'); updateActive(); }
      if (event.key === 'Enter' && state.open && state.active >= 0 && shown[state.active]) { event.preventDefault(); choose(shown[state.active]); }
    });
    list.addEventListener('mousedown', function (event) { if (event.target instanceof Element && event.target.closest('[data-model-index]')) event.preventDefault(); });
    root.addEventListener('focusout', function () { requestAnimationFrame(function () { var wrap = one('.etf-search-wrap'); if (!wrap || !wrap.contains(document.activeElement)) closeSearch(); }); });
    root.dataset.fitReady = 'true';
    all('[data-fit-js]').forEach(function (node) { node.hidden = false; });
    render();
    setText('[data-fit-catalogue-status]', message('catalogue_loading'));
    catalogue(root.dataset.catalogue).then(function (data) {
      if (!root.isConnected) return;
      models = data; catalogueState = 'ready'; setText('[data-fit-count]', String(data.length));
      setText('[data-fit-catalogue-status]', message('catalogue_ready', { count: data.length }));
      if (state.query.trim()) renderMatches();
    }).catch(function () {
      if (!root.isConnected) return;
      catalogueState = 'failed'; var catalogueNote = one('[data-fit-catalogue-status]'); if (catalogueNote) catalogueNote.classList.add('is-failed'); setText('[data-fit-catalogue-status]', message('catalogue_failed')); setHidden('[data-fit-count]', true);
      if (state.query.trim()) renderMatches();
    });
  }
  function mount(scope) {
    var roots = [];
    if (scope && scope.matches && scope.matches('[data-fit-studio]')) roots.push(scope);
    if (scope && scope.querySelectorAll) roots = roots.concat(Array.prototype.slice.call(scope.querySelectorAll('[data-fit-studio]')));
    roots.forEach(function (root) {
      try { init(root); } catch (_) {
        // Isolate optional fit enhancement from navigation, product forms and checkout.
        // Server-rendered reference footprints, caveats and WhatsApp remain available.
        root.dataset.fitReady = 'failed';
        var note = root.querySelector('[data-fit-note]');
        if (note) note.textContent = 'The interactive check is unavailable. Compare the reference footprints below or contact us for a fit check.';
      }
    });
  }
  window.ElevenTheoryFitStudio = { mount: mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mount(document); }, { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', function (event) { mount(event.target); });
})();
